/**
 * StickyRenderer - Renders sticky note elements
 * Implements yellow/colored sticky notes with shadow, rounded corners, and text
 */

import Konva from 'konva';
import { BaseRenderer } from '../core/BaseRenderer';
import { EditableElement } from '../editing/types';
import { COLORS, SPACING, SHADOWS, SIZES } from '../utils/constants';

export class StickyRenderer extends BaseRenderer {
  private backgroundRect: Konva.Rect | null = null;
  private textNode: Konva.Text | null = null;

  constructor(element: EditableElement) {
    super(element);
  }

  /**
   * Render the sticky note
   */
  render(): Konva.Node {
    const { width, height, data } = this.element;
    
    // Background rectangle with shadow
    this.backgroundRect = new Konva.Rect({
      width: width,
      height: height,
      fill: data?.backgroundColor || COLORS.YELLOW,
      cornerRadius: data?.cornerRadius !== undefined ? data.cornerRadius : SIZES.STICKY_NOTE_CORNER_RADIUS,
      shadowColor: data?.shadowColor || SHADOWS.STICKY_NOTE.color,
      shadowBlur: data?.shadowBlur || SHADOWS.STICKY_NOTE.blur,
      shadowOpacity: data?.shadowOpacity || SHADOWS.STICKY_NOTE.opacity,
      shadowOffset: {
        x: data?.shadowOffsetX || SHADOWS.STICKY_NOTE.offset.x,
        y: data?.shadowOffsetY || SHADOWS.STICKY_NOTE.offset.y,
      },
    });
    
    // Text content
    const textContent = (data?.text || 'Double-click to edit').trim() || 'Double-click to edit';
    this.textNode = new Konva.Text({
      text: textContent,
      width: width,
      height: height,
      padding: data?.padding || SPACING.STICKY_PADDING,
      fontSize: data?.fontSize || 14,
      fontFamily: data?.fontFamily || 'Inter, sans-serif',
      fontStyle: data?.fontStyle || 'normal',
      fill: data?.fill || COLORS.BLACK,
      align: data?.align || 'left',
      verticalAlign: 'top',
      wrap: 'word',
    });
    
    this._node.add(this.backgroundRect);
    this._node.add(this.textNode);
    
    return this._node;
  }

  /**
   * Update the sticky note with new properties
   */
  update(updates: Partial<EditableElement>): void {
    this.updateElementData(updates);
    
    const { width, height, data } = this.element;
    
    // Update background
    if (this.backgroundRect) {
      if (updates.width !== undefined || updates.height !== undefined) {
        this.backgroundRect.width(width);
        this.backgroundRect.height(height);
      }
      
      if (updates.data) {
        if (updates.data.backgroundColor !== undefined) {
          this.backgroundRect.fill(updates.data.backgroundColor);
        }
        if (updates.data.cornerRadius !== undefined) {
          this.backgroundRect.cornerRadius(updates.data.cornerRadius);
        }
        if (updates.data.shadowColor !== undefined) {
          this.backgroundRect.shadowColor(updates.data.shadowColor);
        }
        if (updates.data.shadowBlur !== undefined) {
          this.backgroundRect.shadowBlur(updates.data.shadowBlur);
        }
        if (updates.data.shadowOpacity !== undefined) {
          this.backgroundRect.shadowOpacity(updates.data.shadowOpacity);
        }
        if (updates.data.shadowOffsetX !== undefined || updates.data.shadowOffsetY !== undefined) {
          this.backgroundRect.shadowOffset({
            x: data?.shadowOffsetX || SHADOWS.STICKY_NOTE.offset.x,
            y: data?.shadowOffsetY || SHADOWS.STICKY_NOTE.offset.y,
          });
        }
      }
    }
    
    // Update text
    if (this.textNode) {
      if (updates.width !== undefined || updates.height !== undefined) {
        this.textNode.width(width);
        this.textNode.height(height);
      }
      
      if (updates.data) {
        if (updates.data.text !== undefined) {
          const textContent = (updates.data.text || 'Double-click to edit').trim() || 'Double-click to edit';
          this.textNode.text(textContent);
        }
        if (updates.data.fontSize !== undefined) {
          this.textNode.fontSize(updates.data.fontSize);
        }
        if (updates.data.fontFamily !== undefined) {
          this.textNode.fontFamily(updates.data.fontFamily);
        }
        if (updates.data.fontStyle !== undefined) {
          this.textNode.fontStyle(updates.data.fontStyle);
        }
        if (updates.data.fill !== undefined) {
          this.textNode.fill(updates.data.fill);
        }
        if (updates.data.align !== undefined) {
          this.textNode.align(updates.data.align);
        }
        if (updates.data.padding !== undefined) {
          this.textNode.padding(updates.data.padding);
        }
      }
    }
  }

  /**
   * Set dimensions and update internal nodes
   */
  setDimensions(width: number, height: number): void {
    super.setDimensions(width, height);
    
    if (this.backgroundRect) {
      this.backgroundRect.width(width);
      this.backgroundRect.height(height);
    }
    
    if (this.textNode) {
      this.textNode.width(width);
      this.textNode.height(height);
    }
  }

  /**
   * Hide text (for text editing overlay)
   */
  hideText(): void {
    if (this.textNode) {
      this.textNode.visible(false);
    }
  }

  /**
   * Show text
   */
  showText(): void {
    if (this.textNode) {
      this.textNode.visible(true);
    }
  }

  /**
   * Get background color
   */
  getBackgroundColor(): string {
    return this.element.data?.backgroundColor || COLORS.YELLOW;
  }

  /**
   * Get text content
   */
  getText(): string {
    return this.element.data?.text || '';
  }

  /**
   * Destroy the renderer
   */
  destroy(): void {
    this.backgroundRect = null;
    this.textNode = null;
    super.destroy();
  }
}
